import React, { useState, useEffect } from 'react';
import api, { uploadMedia } from '../utils/api';
import './EditFacultyDialog.css';

const EditFacultyDialog = ({ faculty, isOpen, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    coverImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (faculty && isOpen) {
      setFormData({
        name: faculty.name || '',
        nameEn: faculty.nameEn || '',
        coverImage: faculty.coverImage || ''
      });
      setError('');
    }
  }, [faculty, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCoverSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await uploadMedia(file);
      if (result.success) {
        setFormData(prev => ({ ...prev, coverImage: result.url }));
      } else {
        setError(result.error || 'Cover image upload failed.');
      }
    } catch (err) {
      setError(err.message || 'Cover image upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveCover = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.put(`/faculties/${faculty._id}`, formData);
      
      if (response.data.success) {
        onSave(response.data.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update faculty');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Invalid data. Please check your input.');
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Faculty not found.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !deleteLoading) {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this faculty? This action cannot be undone.')) {
      return;
    }

    setDeleteLoading(true);
    setError('');

    try {
      const response = await api.delete(`/faculties/${faculty._id}`);
      
      if (response.data.success) {
        onDelete(faculty._id);
        onClose();
      } else {
        setError(response.data.message || 'Failed to delete faculty');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError('Invalid faculty ID.');
      } else if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Faculty not found.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isOpen || !faculty) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Edit Faculty</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="name">Faculty Name (Bengali)</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter faculty name in Bengali"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="nameEn">Faculty Name (English)</label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              value={formData.nameEn}
              onChange={handleChange}
              required
              placeholder="Enter faculty name in English"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="editCoverImage">Faculty Card Image</label>
            <div className={`cover-upload ${formData.coverImage ? 'has-image' : ''}`}>
              {formData.coverImage ? (
                <img src={formData.coverImage} alt="Faculty cover" loading="lazy" decoding="async" />
              ) : (
                <div className="cover-placeholder">
                  <span className="cover-icon">+</span>
                  <span>Upload cover image</span>
                </div>
              )}
            </div>
            <div className="cover-actions">
              <label className={`upload-cover-button ${uploading || loading ? 'disabled' : ''}`} htmlFor="editCoverImage">
                {uploading ? 'Uploading...' : formData.coverImage ? 'Change Image' : 'Upload Image'}
              </label>
              {formData.coverImage && (
                <button type="button" className="remove-cover-button" onClick={handleRemoveCover} disabled={loading || uploading}>
                  Remove
                </button>
              )}
            </div>
            <input
              type="file"
              id="editCoverImage"
              accept="image/*"
              onChange={handleCoverSelect}
              disabled={loading || uploading}
              className="file-input-hidden"
            />
          </div>
          
          <div className="dialog-actions">
            <button 
              type="button" 
              className="delete-button"
              onClick={handleDelete}
              disabled={loading || deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete Faculty'}
            </button>
            <div className="action-buttons">
              <button 
                type="button" 
                className="cancel-button"
                onClick={handleClose}
                disabled={loading || deleteLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-button"
                disabled={loading || uploading || deleteLoading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFacultyDialog;
